import React, {
    useEffect,
    useMemo,
    useState
} from "react"
import {
    Box,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Skeleton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Tooltip,
    CircularProgress
} from "@mui/material"
import {
    SearchOutlined,
    RefreshOutlined,
    StorageOutlined,
    TableChartOutlined,
    FileDownloadOutlined,
    DescriptionOutlined,
    GridOnOutlined,
    FolderZipOutlined,
    KeyboardArrowDownOutlined,
    FileUploadOutlined
} from "@mui/icons-material"
import { Message, toaster } from "rsuite"
import { useSearchParams } from "react-router-dom"
import _ from "lodash"
import {
    useGetDatabaseTablesQuery,
    useGetDatabaseTableRowsQuery,
    useDownloadDatabaseExportMutation
} from "../Redux/actions/setting.action"
import { saveBlob } from "../StockManager/classroom/helpers"
import DatabaseImportDialog from "./DatabaseImportDialog"

const PAGE_SIZES = [25, 50, 100, 200]
const CELL_PREVIEW_LENGTH = 80

const number = new Intl.NumberFormat("en-IN")

const errorMessage = (error, fallback) => {
    const detail = _.get(error, "data.detail")
    return _.isString(detail) ? detail : fallback
}

const notify = (type, message) =>
    toaster.push(
        <Message showIcon type={type}>{message}</Message>,
        { placement: "topCenter" }
    )

const today = () => new Date().toISOString().slice(0, 10)

const primaryButtonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    px: 2.5,
    bgcolor: "var(--color-primary)",
    boxShadow: "none",
    whiteSpace: "nowrap",
    "&:hover": { bgcolor: "var(--color-primary-hover)", boxShadow: "none" }
}

const outlinedButtonSx = {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "10px",
    whiteSpace: "nowrap",
    borderColor: "var(--border-color)",
    color: "var(--text-primary)"
}

const searchFieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#f8fafc" } }

function useDebounced(value, delay = 400) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(timer)
    }, [value, delay])
    return debounced
}

/* =========================
   DOWNLOAD MENU
========================= */

function DownloadMenu({ label, options, tableName, variant = "contained" }) {
    const [anchor, setAnchor] = useState(null)
    const [download, downloadState] = useDownloadDatabaseExportMutation()

    const handleDownload = async (option) => {
        setAnchor(null)
        try {
            const blob = await download({ table_name: tableName, format: option.format }).unwrap()
            saveBlob(blob, `${tableName || "database"}_${today()}.${option.format}`)
        } catch (error) {
            notify("error", errorMessage(error, "Could not download the data. Please try again."))
        }
    }

    return (
        <>
            <Button
                variant={variant}
                onClick={(e) => setAnchor(e.currentTarget)}
                disabled={downloadState.isLoading}
                startIcon={downloadState.isLoading
                    ? <CircularProgress size={16} color="inherit" />
                    : <FileDownloadOutlined />}
                endIcon={<KeyboardArrowDownOutlined />}
                sx={variant === "contained" ? primaryButtonSx : outlinedButtonSx}
            >
                {downloadState.isLoading ? "Preparing…" : label}
            </Button>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: { borderRadius: "12px", mt: 0.5 } }}
            >
                {options.map((option) => (
                    <MenuItem key={option.format} onClick={() => handleDownload(option)}>
                        <ListItemIcon>{option.icon}</ListItemIcon>
                        <ListItemText
                            primary={option.label}
                            secondary={option.description}
                            primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                            secondaryTypographyProps={{ fontSize: 12 }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}

const ALL_TABLE_OPTIONS = [
    {
        format: "xlsx",
        label: "Excel workbook",
        description: "One sheet per table",
        icon: <GridOnOutlined fontSize="small" />
    },
    {
        format: "zip",
        label: "ZIP of CSV files",
        description: "One CSV per table",
        icon: <FolderZipOutlined fontSize="small" />
    }
]

const SINGLE_TABLE_OPTIONS = [
    {
        format: "csv",
        label: "CSV",
        description: "Opens in any spreadsheet app",
        icon: <DescriptionOutlined fontSize="small" />
    },
    {
        format: "xlsx",
        label: "Excel",
        description: ".xlsx workbook",
        icon: <GridOnOutlined fontSize="small" />
    }
]

/* =========================
   TABLE DATA VIEWER
========================= */

function CellValue({ value }) {
    if (value === null || value === undefined) {
        return <Box component="span" sx={{ color: "var(--text-light)", fontStyle: "italic" }}>NULL</Box>
    }
    const text = String(value)
    if (text.length <= CELL_PREVIEW_LENGTH) {
        return text
    }
    return (
        <Tooltip title={<Box sx={{ whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}>{text}</Box>}>
            <span>{text.slice(0, CELL_PREVIEW_LENGTH)}…</span>
        </Tooltip>
    )
}

function TableViewer({ table }) {
    const [importing, setImporting] = useState(false)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[1])
    const [search, setSearch] = useState("")
    const debouncedSearch = useDebounced(search.trim())

    useEffect(() => {
        setPage(0)
    }, [debouncedSearch])

    const { data, isLoading, isFetching, isError, error, refetch } = useGetDatabaseTableRowsQuery({
        table_name: table.name,
        page: page + 1,
        page_size: pageSize,
        search: debouncedSearch
    })

    const columns = _.get(data, "columns", table.columns)
    const rows = _.get(data, "rows", [])
    const total = _.get(data, "total", 0)
    const primaryKey = table.primary_key || []

    return (
        <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid var(--border-color)", overflow: "hidden", minWidth: 0 }}>
            <Box sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                gap: 1.5,
                p: 2,
                borderBottom: "1px solid var(--border-color)"
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 18 }}>
                        {table.name}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "var(--text-secondary)" }}>
                        {number.format(table.row_count)} rows · {table.columns.length} columns
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search rows"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ ...searchFieldSx, flex: 1, minWidth: { md: 220 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton
                                onClick={refetch}
                                disabled={isFetching}
                                sx={{ border: "1px solid var(--border-color)", borderRadius: "10px" }}
                                aria-label="Refresh rows"
                            >
                                <RefreshOutlined fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<FileUploadOutlined />}
                        onClick={() => setImporting(true)}
                        sx={outlinedButtonSx}
                    >
                        Import
                    </Button>
                    <DownloadMenu
                        label="Download"
                        variant="outlined"
                        tableName={table.name}
                        options={SINGLE_TABLE_OPTIONS}
                    />
                </Box>
            </Box>

            {importing && (
                <DatabaseImportDialog tableName={table.name} onClose={() => setImporting(false)} />
            )}

            {isError ? (
                <Box sx={{ py: 6, px: 3, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1 }}>Couldn't load rows</Typography>
                    <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", mb: 2 }}>
                        {errorMessage(error, "Check that the server is running, then try again.")}
                    </Typography>
                    <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={refetch} sx={{ textTransform: "none" }}>
                        Try again
                    </Button>
                </Box>
            ) : isLoading ? (
                <Box sx={{ p: 2 }}>
                    {[0, 1, 2, 3, 4].map((row) => (
                        <Skeleton key={row} height={44} sx={{ borderRadius: "8px" }} />
                    ))}
                </Box>
            ) : rows.length === 0 ? (
                <Box sx={{ py: 7, px: 3, textAlign: "center" }}>
                    <TableChartOutlined sx={{ fontSize: 40, color: "var(--text-light)", mb: 1 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                        {debouncedSearch ? "No rows match your search" : "This table is empty"}
                    </Typography>
                    {debouncedSearch && (
                        <Button onClick={() => setSearch("")} sx={{ textTransform: "none", fontWeight: 600, mt: 1 }}>
                            Clear search
                        </Button>
                    )}
                </Box>
            ) : (
                <TableContainer sx={{ maxHeight: "60vh", opacity: isFetching ? 0.6 : 1, transition: "opacity .15s" }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{
                                "& th": {
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "var(--text-secondary)",
                                    bgcolor: "#f8fafc",
                                    borderBottom: "1px solid var(--border-color)",
                                    whiteSpace: "nowrap",
                                    verticalAlign: "top"
                                }
                            }}>
                                {columns.map((column) => (
                                    <TableCell key={column.name}>
                                        {column.name}
                                        {primaryKey.includes(column.name) && (
                                            <Box component="span" sx={{ ml: 0.75, fontSize: 10, color: "var(--color-primary)" }}>PK</Box>
                                        )}
                                        <Typography sx={{ fontSize: 11, fontWeight: 500, color: "var(--text-light)", textTransform: "lowercase" }}>
                                            {column.type}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => (
                                <TableRow
                                    key={primaryKey.length ? primaryKey.map((key) => row[key]).join("|") : index}
                                    hover
                                    sx={{ "& td": { borderBottom: "1px solid #f1f5f9", fontSize: 13, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } }}
                                >
                                    {columns.map((column) => (
                                        <TableCell key={column.name}>
                                            <CellValue value={row[column.name]} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!isError && total > 0 && (
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_e, next) => setPage(next)}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={PAGE_SIZES}
                    onRowsPerPageChange={(e) => {
                        setPageSize(Number(e.target.value))
                        setPage(0)
                    }}
                    sx={{ borderTop: "1px solid var(--border-color)" }}
                />
            )}
        </Paper>
    )
}

/* =========================
   PAGE
========================= */

export default function Database() {
    const { data, isLoading, isFetching, isError, error, refetch } = useGetDatabaseTablesQuery()
    const [searchParams, setSearchParams] = useSearchParams()
    const [filter, setFilter] = useState("")
    const [importingAll, setImportingAll] = useState(false)

    const tables = useMemo(() => _.get(data, "list", []) || [], [data])

    const visibleTables = useMemo(() => {
        const query = filter.trim().toLowerCase()
        return tables.filter((table) => !query || table.name.toLowerCase().includes(query))
    }, [tables, filter])

    const selectedName = searchParams.get("table")
    const selected = tables.find((table) => table.name === selectedName) || tables[0]

    const selectTable = (name) => setSearchParams({ table: name }, { replace: true })

    if (isError) {
        return (
            <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "16px", border: "1px solid var(--border-color)" }}>
                <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
                    Couldn't load the database tables
                </Typography>
                <Typography sx={{ color: "var(--text-secondary)", fontSize: 14, mb: 3 }}>
                    {errorMessage(error, "Check that the server is running, then try again.")}
                </Typography>
                <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={refetch} sx={{ textTransform: "none" }}>
                    Try again
                </Button>
            </Paper>
        )
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* ================= SUMMARY ================= */}

            <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr) auto" },
                gap: 2,
                alignItems: "stretch"
            }}>
                {[
                    { label: "Database", value: _.get(data, "database", "—") },
                    { label: "Tables", value: number.format(tables.length) },
                    { label: "Total rows", value: number.format(_.get(data, "total_rows", 0)) }
                ].map((card, index) => (
                    <Paper
                        key={card.label}
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: "14px",
                            border: "1px solid var(--border-color)",
                            gridColumn: { xs: index === 0 ? "1 / -1" : "auto", md: "auto" },
                            minWidth: 0
                        }}
                    >
                        <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 }}>
                            {card.label}
                        </Typography>
                        {isLoading ? (
                            <Skeleton width={80} height={36} />
                        ) : (
                            <Typography
                                noWrap
                                sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font-heading)" }}
                            >
                                {card.value}
                            </Typography>
                        )}
                    </Paper>
                ))}
                <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "row", md: "column" },
                    gap: 1,
                    gridColumn: { xs: "1 / -1", md: "auto" },
                    "& > button": { flex: 1 }
                }}>
                    <DownloadMenu label="Download all tables" options={ALL_TABLE_OPTIONS} />
                    <Button
                        variant="outlined"
                        startIcon={<FileUploadOutlined />}
                        onClick={() => setImportingAll(true)}
                        sx={outlinedButtonSx}
                    >
                        Import tables
                    </Button>
                </Box>
            </Box>

            {/* ================= TABLES + DATA ================= */}

            <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr)" },
                gap: 2.5,
                alignItems: "start"
            }}>
                <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid var(--border-color)", overflow: "hidden" }}>
                    <Box sx={{ display: "flex", gap: 1, p: 1.5, borderBottom: "1px solid var(--border-color)" }}>
                        <TextField
                            size="small"
                            placeholder="Find a table"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            sx={{ ...searchFieldSx, flex: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlined fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Tooltip title="Refresh tables">
                            <span>
                                <IconButton
                                    onClick={refetch}
                                    disabled={isFetching}
                                    sx={{ border: "1px solid var(--border-color)", borderRadius: "10px" }}
                                    aria-label="Refresh tables"
                                >
                                    <RefreshOutlined fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    <Box sx={{ maxHeight: { xs: 260, md: "66vh" }, overflowY: "auto" }}>
                        {isLoading ? (
                            <Box sx={{ p: 1.5 }}>
                                {[0, 1, 2, 3, 4, 5].map((row) => (
                                    <Skeleton key={row} height={40} sx={{ borderRadius: "8px" }} />
                                ))}
                            </Box>
                        ) : visibleTables.length === 0 ? (
                            <Typography sx={{ p: 3, fontSize: 13, color: "var(--text-secondary)", textAlign: "center" }}>
                                {tables.length === 0 ? "The database has no tables." : "No tables match."}
                            </Typography>
                        ) : (
                            visibleTables.map((table) => {
                                const active = selected && selected.name === table.name
                                return (
                                    <Box
                                        key={table.name}
                                        component="button"
                                        type="button"
                                        onClick={() => selectTable(table.name)}
                                        aria-current={active ? "true" : undefined}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.25,
                                            width: "100%",
                                            px: 1.75,
                                            py: 1.25,
                                            border: "none",
                                            borderLeft: "3px solid",
                                            borderLeftColor: active ? "var(--color-primary)" : "transparent",
                                            bgcolor: active ? "var(--color-primary-light)" : "transparent",
                                            cursor: "pointer",
                                            textAlign: "left",
                                            font: "inherit",
                                            "&:hover": { bgcolor: active ? "var(--color-primary-light)" : "#f8fafc" }
                                        }}
                                    >
                                        <StorageOutlined
                                            fontSize="small"
                                            sx={{ color: active ? "var(--color-primary)" : "var(--text-light)" }}
                                        />
                                        <Typography noWrap sx={{
                                            flex: 1,
                                            fontSize: 13,
                                            fontWeight: active ? 700 : 500,
                                            color: active ? "var(--color-primary)" : "var(--text-primary)"
                                        }}>
                                            {table.name}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: "var(--text-light)", fontVariantNumeric: "tabular-nums" }}>
                                            {number.format(table.row_count)}
                                        </Typography>
                                    </Box>
                                )
                            })
                        )}
                    </Box>
                </Paper>

                {selected ? (
                    <TableViewer key={selected.name} table={selected} />
                ) : !isLoading && (
                    <Paper elevation={0} sx={{ py: 7, textAlign: "center", borderRadius: "16px", border: "1px solid var(--border-color)" }}>
                        <TableChartOutlined sx={{ fontSize: 40, color: "var(--text-light)", mb: 1 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>No table selected</Typography>
                    </Paper>
                )}
            </Box>

            {importingAll && <DatabaseImportDialog onClose={() => setImportingAll(false)} />}
        </Box>
    )
}
